import moment from 'moment';

export const getStartDate = (startDate, criteria) =>
  criteria === 'MONTH'
    ? moment(startDate).clone().add(1, 'month').startOf('month')
    : moment(startDate).clone().startOf('month');

export const getEndDate = (endDate) => {
  endDate = moment(endDate).endOf('day');
  const endOfMonth = endDate.clone().endOf('month');
  const startOfMonth = endDate.clone().startOf('month');
  return endDate.isSame(endOfMonth) ? endOfMonth.clone().add(1, 'day') : startOfMonth;
};

// Same engine as the backend (src/lib/depreciation.js). Keep both in step:
// the asset screen uses this copy and the reports use the backend one.

/**
 * Useful life in months under LISR straight line: 1200 / annual rate.
 *
 * Rules store the annual rate repeated and the remainder in the last year
 * (35/35/30, 30/30/30/10): only percentages[0] is a rate. The life is not
 * always a whole number of months (35% -> 34.29).
 */
export const getLifeMonths = (rates) => 1200 / rates.percentages[0];

/**
 * Months to depreciate up to the cut date, capped at the useful life. It may
 * return a fraction (34.29): getDepreciationDates rounds it up, and that last
 * month only carries the remainder (see getDepreciation).
 *
 * `years` and `price` are no longer used; they stay in the signature so the
 * callers do not change.
 */
export const getMonthsCount = (startDate, currentDate, years, price, rates) => {
  const total = getLifeMonths(rates);
  const months = moment(currentDate).diff(moment(startDate), 'months');
  return months >= total ? total : months;
};

export const getMonthlyDepreciation = (amount, percentage) => {
  const monthlyPercentage = percentage / 12;
  const depreciation = (monthlyPercentage / 100) * amount;
  const rounded = Math.round(depreciation * 1e4) / 1e4;
  return { depreciation: rounded, percentage: monthlyPercentage };
};

export const getMonthlyDepreciationByYear = (amount, percentages) =>
  percentages.map(getMonthlyDepreciation.bind(null, amount));

// A fractional life (34.29) generates 35 dates; the last one is the remainder.
export const getDepreciationDates = (months, startDate) => {
  const dates = [];
  const current = moment(startDate);
  const count = Math.ceil(months);
  for (let i = 0; i < count; i++) {
    dates.push(current.clone());
    current.add(1, 'month');
  }
  return dates;
};

export const round = (amount, decimals) =>
  decimals === 0 ? Math.round(amount) : Math.round(amount * 10 ** decimals) / 10 ** decimals;

export const roundResult = (result) => {
  result.totalPercentage = round(result.totalPercentage, 2);
  result.totalDepreciation = round(result.totalDepreciation, 2);
  result.depreciable = round(result.depreciable, 2);
  result.originalAmount = round(result.originalAmount, 2);
  result.years = round(result.years, 2);
  if (result.refound) result.refound = round(result.refound, 2);

  Object.keys(result.yearly).forEach((y) => {
    result.yearly[y].factors.inpcBegin = result.yearly[y].factors.inpcBegin.toFixed(4);
    result.yearly[y].factors.inpcEnd = result.yearly[y].factors.inpcEnd.toFixed(4);
    result.yearly[y].factors.factor = result.yearly[y].factors.factor.toFixed(4);

    result.yearly[y].depreciation = round(result.yearly[y].depreciation, 2);
    result.yearly[y].depreciationPercentage = round(result.yearly[y].depreciationPercentage, 2);
    result.yearly[y].acummDepreciation = round(result.yearly[y].acummDepreciation, 2);
    result.yearly[y].acummDepreciationPercentage = round(
      result.yearly[y].acummDepreciationPercentage,
      4
    );
    result.yearly[y].depreciable = round(result.yearly[y].depreciable, 2);

    Object.keys(result.yearly[y].monthly).forEach((m) => {
      result.yearly[y].monthly[m].depreciation = round(result.yearly[y].monthly[m].depreciation, 2);
      result.yearly[y].monthly[m].acummDepreciation = round(
        result.yearly[y].monthly[m].acummDepreciation,
        2
      );
      result.yearly[y].monthly[m].acummDepreciationPercentage = round(
        result.yearly[y].monthly[m].acummDepreciationPercentage,
        4
      );
      result.yearly[y].monthly[m].depreciable = round(result.yearly[y].monthly[m].depreciable, 2);
    });
  });

  return result;
};

export const getRangesByYear = (dates, firstYear) =>
  dates.reduce((result, date) => {
    const year = date.get('year');
    const month = date.get('month') + 1;
    result[year] = result[year] || {
      firstMonth: month,
      isFirstYear: firstYear === year,
    };
    result[year].lastMonth = month;
    return result;
  }, {});

export const getFactors = (startDate, dates, inpcs = {}, fiscal = false) => {
  const [firstDate] = dates;

  if (!firstDate) return null;

  const firstYear = firstDate.get('year');
  const startYear = startDate.get('year');
  const startMonth = startDate.get('month') + 1;
  const inpcBegin = (inpcs[startYear] && inpcs[startYear][startMonth]) || 1;

  let ranges = getRangesByYear(dates, firstYear);

  ranges = Object.keys(ranges).reduce((result, year) => {
    result[year] = ranges[year];
    const { isFirstYear, lastMonth, firstMonth } = result[year];
    const inpc = inpcs[year] || {};
    const countMonthDep = Math.floor(lastMonth - firstMonth + 1);
    const half = Math.floor((lastMonth - firstMonth) / 2);
    const monthInHalf = isFirstYear ? half + firstMonth : 6;
    const inpcEnd = inpc[monthInHalf] || 1;
    const warning = (inpcEnd === 1 || inpcBegin === 1) && fiscal;
    let factor = warning ? 1 : round(inpcEnd / inpcBegin, 4);
    factor = factor < 1 ? 1 : factor;
    result[year] = {
      ...result[year],
      monthInHalf,
      inpcEnd,
      inpcBegin,
      warning,
      factor,
      countMonthDep,
    };

    return result;
  }, {});

  return ranges;
};

/**
 * Straight line: the same monthly amount (monthlyDep[0], rate / 12) every
 * month until the base is reached. The month that completes the useful life
 * takes exactly the remainder, so a finished asset closes at 100% of its base.
 *
 * It used to pick the rate with `monthlyDep[Math.floor(index / 14)]`: it took
 * the "rate" of the next year every 14 months (not 12), treated the last
 * percentage (the remainder, e.g. 30 in 35/35/30) as a rate, and threw a
 * TypeError when the index ran past the array (10/30/30/30). Changing it to /12
 * was ruled out: the later percentages are not rates.
 *
 * @param {Number} lifeMonths - getLifeMonths; Infinity when it is unknown
 */
export const getDepreciation = (
  amount,
  dates,
  monthlyDep,
  factors,
  maxDep,
  lifeMonths = Infinity
) =>
  dates.reduce(
    (result, date, index) => {
      const year = date.get('year');
      const month = date.get('month') + 1;

      result.yearly[year] = result.yearly[year] || {
        normalDepreciation: 0,
        depreciation: 0,
        depreciationPercentage: 0,
        acummDepreciation: 0,
        acummDepreciationPercentage: 0,
        depreciable: 0,
        monthly: {},
      };

      const monthly = monthlyDep[0];
      const remaining = amount - maxDep - result.totalDepreciation;
      const isLastLifeMonth = index + 1 >= lifeMonths;
      const depreciation = isLastLifeMonth
        ? Math.max(remaining, 0)
        : Math.max(Math.min(monthly.depreciation, remaining), 0);
      // The last month closes the percentage at 100 too: scaling it by the
      // rounded monthly amount left a $1 asset at 100.16%.
      const percentage = isLastLifeMonth
        ? Math.max(100 - result.totalPercentage, 0)
        : depreciation === monthly.depreciation || !monthly.depreciation
        ? monthly.percentage
        : monthly.percentage * (depreciation / monthly.depreciation);

      const updatedDepreciation = depreciation;
      const normalDepreciation = depreciation;
      result.totalPercentage += percentage;
      result.totalDepreciation += updatedDepreciation;
      result.depreciable -= updatedDepreciation;
      result.depreciable = result.depreciable < 0 ? 0 : result.depreciable;

      result.yearly[year].factors = factors[year];
      result.yearly[year].normalDepreciation += normalDepreciation;
      result.yearly[year].depreciation += updatedDepreciation;
      result.yearly[year].depreciationPercentage += percentage;
      result.yearly[year].acummDepreciation = result.totalDepreciation;
      result.yearly[year].acummDepreciationPercentage = result.totalPercentage;
      result.yearly[year].depreciable = result.depreciable;

      result.yearly[year].monthly[month] = {
        depreciation: updatedDepreciation,
        factorDepreciation: factors[year].factor,
        originalPeriodDepreciation: depreciation.depreciation,
        acummDepreciation: result.totalDepreciation,
        acummDepreciationPercentage: result.totalPercentage,
        depreciable: result.depreciable,
      };

      return result;
    },
    {
      totalPercentage: 0,
      totalDepreciation: 0,
      depreciable: amount - maxDep,
      originalAmount: amount,
      yearly: {},
    }
  );

/**
 * When the useful life is complete, the last year absorbs the rounding of the
 * previous ones so the yearly amounts add up exactly to the total depreciated
 * (the MOI): 2,321.77 + 5,572.25 + 5,572.25 + 2,454.43 = 15,920.70. Without
 * it the last year showed 2,454.44 and the years added up to one cent more.
 * Only applies to a finished life: before that no year is "the last" one.
 */
export const closeLastYear = (result) => {
  const years = Object.keys(result.yearly);
  if (!years.length) return result;
  const last = years[years.length - 1];
  const previous = years.slice(0, -1).reduce((sum, y) => sum + result.yearly[y].depreciation, 0);
  const closing = round(result.totalDepreciation - previous, 2);
  result.yearly[last].depreciation = closing;
  result.yearly[last].normalDepreciation = closing;
  return result;
};

export const lastPeriodDepreciations = (depreciation) => {
  if (!depreciation) return null;

  const years = Object.keys(depreciation.yearly);

  let lastYear;

  years.forEach((y) => {
    lastYear = y;
    depreciation.yearly[y].lastPeriodDepreciation = depreciation.yearly[y - 1]
      ? depreciation.yearly[y - 1].acummDepreciation
      : 0;
  });

  if (lastYear && depreciation.yearly[lastYear]) {
    depreciation.lastPeriodDepreciation = depreciation.yearly[lastYear].lastPeriodDepreciation;
    depreciation.currentNormalDepreciation = depreciation.yearly[lastYear].normalDepreciation;
    depreciation.currentDepreciation = depreciation.yearly[lastYear].depreciation;
    depreciation.factors = depreciation.yearly[lastYear].factors;
  }

  return depreciation;
};

/**
 * Method to calculate the depreciation of an Asset,
 * given the Depreciation object and a flag to use or no the inpcs table
 *
 * @param {Asset} asset
 * @param {Depreciation} depreciation
 * @param {Boolean} isFiscal
 * @param {Object} Inpcs
 * @param {Date} endDate
 */
export const getAssetDepreciation = (asset, depreciation, isFiscal, inpcs, endDate) => {
  if (!depreciation) return null;
  let depEndDate;
  let { price, exchange, startDate, disableDate, disableAmount, dollarAssetPrice } = asset;
  let {
    rates: [rates],
    criteria,
    maximumAmount = 0,
    maximumPercent = 0,
  } = depreciation;
  const depStartDate = getStartDate(startDate, criteria);

  if (disableDate && moment(disableDate).isBefore(endDate)) {
    depEndDate = getEndDate(disableDate);
  } else {
    depEndDate = getEndDate(endDate);
  }

  startDate = moment(startDate);

  exchange = exchange || 1;

  let result = null;
  if (disableDate) {
    result = { refound: disableAmount - (price || 0) };
  }

  if (!rates || !rates.percentages || !startDate || !price) return result;
  if (depStartDate.isAfter(depEndDate)) return result;

  let moi = price * exchange;
  let amount = price * exchange;

  if (maximumPercent) {
    maximumPercent = maximumPercent ? amount * ((100 - maximumPercent) / 100) : 0;
  }

  if (maximumAmount) {
    amount = maximumAmount - amount <= 0 ? maximumAmount : amount;
  }

  const months = getMonthsCount(depStartDate, depEndDate, rates.percentages.length, price, rates);
  const monthlyDep = getMonthlyDepreciationByYear(amount - maximumPercent, rates.percentages);
  const dates = getDepreciationDates(months, depStartDate);
  const factors = getFactors(startDate, dates, isFiscal ? inpcs : undefined, isFiscal);
  const usefulLifeMonths = getLifeMonths(rates);
  result = getDepreciation(amount, dates, monthlyDep, factors, maximumPercent, usefulLifeMonths);
  let lifeMonths = 12 * rates.percentages.length - months;
  let lifeYears = (lifeMonths / 12).toFixed(4);
  let depYearly = rates.percentages[0] * (price / 100);
  let ednDep = depYearly / 12;
  let totalLifeMonths = price / ednDep;
  // MOI x rate / 12, the same as the backend (it used the price without the
  // exchange rate).
  let depMonthly = (moi * rates.percentages[0]) / 1200;
  let depMonthlyUsd = (dollarAssetPrice * rates.percentages[0]) / 1200;

  result.depMonthlyUsd = depMonthlyUsd;
  result.ednDep = ednDep;
  result.totalLifeMonths = totalLifeMonths;
  result.depMonthly = depMonthly;
  result.months = months;
  result.years = months / 12;
  result.startDate = depStartDate;
  result.endDate = depEndDate;
  result.lifeMonths = lifeMonths;
  result.lifeYears = lifeYears;
  result.totalLifeYears = rates.percentages.length;
  result.name = depreciation.name;
  result.originalAmount = moi;

  if (disableDate) {
    result.refound = disableAmount - result.depreciable;
  }

  result = roundResult(result);
  if (months >= usefulLifeMonths) closeLastYear(result);
  result = lastPeriodDepreciations(result);
  return result;
};

export const getLocationPath = (asset = {}) => {
  const { location = '' } = asset;
  return location.split('.').filter((p) => !!p);
};

export const getFirstImage = (_files) => {
  let file = _files.find(
    (f) =>
      ['png', 'jpeg', 'jpg', 'gif'].indexOf(f.name.split('.').pop().toLowerCase()) > -1 && f.default
  );

  file =
    file ||
    _files.find(
      (f) => ['png', 'jpeg', 'jpg', 'gif'].indexOf(f.name.split('.').pop().toLowerCase()) > -1
    );
  return file;
};



// WEBPACK FOOTER //
// src/lib/assets/assets_helpers.js